import { getStoredUtmParams, getStoredRefCode } from './utmService';
import { inferOriginTypeFromPath } from '../../config/onboardingOrigin';

/**
 * Shape of the JSON body posted to `POST /api/auth/complete` by every caller.
 *
 * The backend reads each field via `request_data.get(...)` (see
 * `backend/routes/auth.py::complete_authentication`) and passes UTM /
 * acquisition fields through to `provision_new_account`, where they become
 * `User.skeleton.acquisition.*`. Fields left `undefined` are omitted by
 * `JSON.stringify` and treated as `None` server-side.
 */
export interface AuthCompletePayload {
  skip_space_creation: boolean;
  funnel_id?: string;
  persona?: string;
  page_group?: string;
  page_slug?: string;
  /** Content category of the originating page (e.g. 'creative'). Used to set defaultModel. */
  category?: string;
  onboarding_data?: { role?: string; interest?: string };
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  first_landing_page?: string;
  referrer?: string;
  ref_code?: string;
  /** Dark-mode default for new users arriving from creative/NSFW routes. */
  signup_theme?: 'dark';
  /** Page origin metadata captured from HTML head at signup click time. */
  origin?: Record<string, unknown>;
  /**
   * Raw Auth0 ID token for the identity that just authenticated. Only needed
   * when the backend detects a same-email conflict under a different
   * provider and decides to auto-link this identity onto an existing
   * account — see backend/services/account_linking_service.py. Omit when
   * there is no live ID token available (the backend simply blocks the
   * rarer link case with a clear error message instead).
   */
  id_token?: string;
}

/**
 * Overrides accepted by the builder. Anything left undefined falls back to
 * the appropriate localStorage / utmService source. Callers that already
 * have values in closure (e.g. `useAuthForm` knows its `funnelId` prop)
 * should pass them so the payload reflects the live React state even if
 * localStorage is stale.
 */
export interface AuthCompletePayloadOverrides {
  skip_space_creation: boolean;
  funnel_id?: string;
  persona?: string;
  page_group?: string;
  page_slug?: string;
  /** Content category of the originating page (e.g. 'creative'). Used to set defaultModel. */
  category?: string;
  onboarding_data?: { role?: string; interest?: string };
  /** Dark-mode default for new users arriving from creative/NSFW routes. */
  signup_theme?: 'dark';
  /** See AuthCompletePayload.id_token. */
  id_token?: string;
}

function readPendingOnboardingData(): { role?: string; interest?: string } | undefined {
  try {
    const stored = localStorage.getItem('pre_signup_onboarding_answers');
    if (!stored) return undefined;
    const parsed = JSON.parse(stored);
    const role = parsed.role || undefined;
    const interest = parsed.interest || parsed.interests || undefined;
    if (!role && !interest) return undefined;
    return { role, interest };
  } catch {
    return undefined;
  }
}

/** Must stay in sync with content-renderer/lib/spaHandoff.ts:PENDING_ORIGIN_KEY. */
export const PENDING_ORIGIN_KEY = 'river_pending_origin';

/**
 * Read origin from the URL param (email/direct signup), or fall back to the
 * localStorage mirror written by spaHandoff.capturePageOrigin() before the
 * Google OAuth redirect (URL params don't survive that round-trip).
 * Always clears the localStorage mirror so it never leaks to a later signup.
 */
function readOrigin(): Record<string, unknown> | undefined {
  // Always clear the localStorage mirror regardless of which path succeeds,
  // so stale values don't affect a later unrelated signup session.
  const clearMirror = () => {
    try { localStorage.removeItem(PENDING_ORIGIN_KEY); } catch { /* ignore */ }
  };

  try {
    const search = new URLSearchParams(window.location.search);
    // 1. URL param — present for email signup and same-page OAuth (freshest, richest source)
    const urlParam = search.get('origin');
    if (urlParam) {
      clearMirror();
      return JSON.parse(urlParam);
    }
    // 2. localStorage mirror — present after Google OAuth redirect (same-origin only)
    const stored = localStorage.getItem(PENDING_ORIGIN_KEY);
    clearMirror();
    if (stored) {
      return JSON.parse(stored);
    }
    // 3. Reconstruct a minimal origin from the landing_path handoff param. This is
    //    the guaranteed fallback for content CTAs whose rich page-captured origin
    //    didn't survive transport — it carries enough (type/path/category) for the
    //    wizard-skip and welcome-override to fire, just without the rich metadata.
    const landingPath = search.get('landing_path');
    const inferredType = inferOriginTypeFromPath(landingPath);
    if (inferredType) {
      const category = search.get('category') ?? undefined;
      return {
        type: inferredType,
        path: landingPath ?? undefined,
        ...(category ? { category } : {}),
      };
    }
  } catch {
    // Malformed JSON or localStorage unavailable
    clearMirror();
  }
  return undefined;
}

/**
 * Build the JSON body for `POST /api/auth/complete`.
 *
 * Why this exists: four call sites in the app post to `/auth/complete` and
 * race each other to provision the same Auth0 user. Whichever wins the
 * unique-index insert in `provision_new_account` is the one whose UTM,
 * funnel, persona, and affiliate ref_code get persisted onto the new
 * `User.skeleton.acquisition`. Any caller that posts a partial body can
 * orphan a new signup with no attribution at all. Centralizing the body
 * here guarantees every caller sends the same complete shape.
 *
 * Order of precedence per field:
 *   1. Explicit override passed in (caller has live closure value)
 *   2. localStorage fallback (set earlier in the flow, e.g. by
 *      `useAuthForm.handleGoogleAuth` before opening the OAuth popup)
 *   3. `undefined` (omitted from the serialized body)
 */
export function buildAuthCompletePayload(
  overrides: AuthCompletePayloadOverrides,
): AuthCompletePayload {
  const utm = getStoredUtmParams();

  // sessionStorage takes priority: guide pages write there to avoid cross-session
  // leakage. useAuthForm.handleGoogleAuth writes to localStorage before the OAuth
  // popup so the value survives the redirect (sessionStorage does not).
  const storedTheme = sessionStorage.getItem('pendingTheme') ?? localStorage.getItem('pendingTheme');
  const signupTheme: 'dark' | undefined =
    overrides.signup_theme ?? (storedTheme === 'dark' ? 'dark' : undefined);

  // When an invite link specifies a space pack, the pack install creates the
  // primary space — skip the default space so the user doesn't end up with an
  // extra empty one. Read and immediately clear the flag: the payload is cached
  // (Google OAuth stores it before the popup opens) so it only needs to be read
  // once. Clearing here prevents a stale flag from affecting a later unrelated signup.
  const pendingInviteHasSpacePack = localStorage.getItem('pendingInviteHasSpacePack');
  if (pendingInviteHasSpacePack) {
    localStorage.removeItem('pendingInviteHasSpacePack');
  }
  const skipForPackInvite = !!pendingInviteHasSpacePack;

  return {
    skip_space_creation: overrides.skip_space_creation || skipForPackInvite,
    funnel_id:
      overrides.funnel_id ?? (localStorage.getItem('pendingFunnelId') || undefined),
    persona:
      overrides.persona ?? (localStorage.getItem('pendingPersona') || undefined),
    page_group:
      overrides.page_group ?? (localStorage.getItem('pendingPageGroup') || undefined),
    page_slug:
      overrides.page_slug ?? (localStorage.getItem('pendingPageSlug') || undefined),
    category:
      overrides.category ?? (sessionStorage.getItem('pendingPageCategory') || localStorage.getItem('pendingPageCategory') || undefined),
    onboarding_data: overrides.onboarding_data ?? readPendingOnboardingData(),
    utm_source: utm?.utm_source,
    utm_medium: utm?.utm_medium,
    utm_campaign: utm?.utm_campaign,
    utm_content: utm?.utm_content,
    utm_term: utm?.utm_term,
    first_landing_page: utm?.first_landing_page,
    referrer: utm?.referrer,
    ref_code: getStoredRefCode() || undefined,
    signup_theme: signupTheme,
    origin: readOrigin(),
    id_token: overrides.id_token,
  };
}
